import React, { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getBookingDetails, cancelBookingDate } from "../../api/apiClient"
import {
  AlertTriangle,
  Clock,
  CalendarX,
  ArrowLeft,
  CheckCircle,
} from "lucide-react"

const BookingDetailsPage = () => {
  const { bookingId } = useParams() // Get the ID from the URL
  const navigate = useNavigate()

  const [booking, setBooking] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // 2. Add state for modal, API loading, and notifications
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dateToCancel, setDateToCancel] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [notification, setNotification] = useState({ message: "", type: "" })

  // 3. Moved fetchDetails outside useEffect so it can be re-called
  const fetchDetails = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await getBookingDetails(bookingId)
      setBooking(data)
    } catch (err) {
      setError(err.message || "Failed to load booking details.")
    } finally {
      setIsLoading(false)
    }
  }, [bookingId])

  // 4. useEffect now just calls fetchDetails on load
  useEffect(() => {
    fetchDetails()
  }, [bookingId, fetchDetails]) // Re-fetch if the ID changes

  // 5. New handlers for the modal and API call
  const openCancelModal = date => {
    setDateToCancel(date)
    setIsModalOpen(true)
    setNotification({ message: "", type: "" }) // Clear old notifications
  }

  const closeModal = () => {
    if (isCancelling) return // Don't close while API call is in progress
    setIsModalOpen(false)
    setDateToCancel(null)
  }

  const confirmCancellation = async () => {
    if (!dateToCancel) return

    setIsCancelling(true)
    setNotification({ message: "", type: "" })

    try {
      const result = await cancelBookingDate(bookingId, dateToCancel)
      setNotification({
        message: result.message || "Cancellation successful.",
        type: "success",
      })
      // Refresh data to show the new "Cancelled" status
      await fetchDetails()
    } catch (err) {
      setNotification({
        message: err.message || "Cancellation failed.",
        type: "error",
      })
    } finally {
      setIsCancelling(false)
      setIsModalOpen(false)
      setDateToCancel(null)
    }
  }

  // --- Rendering States ---
  if (isLoading && !booking) {
    // Only show full-page load spinner on first load
    return (
      <div className="flex items-center justify-center p-12 bg-white rounded-xl shadow-lg">
        <Clock className="w-6 h-6 animate-spin text-indigo-600 mr-3" />
        <p className="text-gray-600">Loading booking details...</p>
      </div>
    )
  }

  if (error && !booking) {
    // Only show full-page error if booking failed to load
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
        <AlertTriangle className="w-5 h-5 text-red-600 inline mr-2" />
        <p className="text-red-700 font-medium">Error: {error}</p>
      </div>
    )
  }

  if (!booking) return null

  // --- Main Render ---
  return (
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <button
        onClick={() => navigate("/bookings")} // Go back to the list
        className="flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to All Bookings
      </button>

      {/* 6. Notification Area */}
      {notification.message && (
        <div
          className={`p-4 mb-4 rounded-lg ${
            notification.type === "success"
              ? "bg-green-50 border border-green-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          {notification.type === "success" ? (
            <CheckCircle className="w-5 h-5 text-green-600 inline mr-2" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-600 inline mr-2" />
          )}
          <p
            className={`inline ${
              notification.type === "success"
                ? "text-green-700"
                : "text-red-700"
            } font-medium`}
          >
            {notification.message}
          </p>
        </div>
      )}

      {/* Booking Summary */}
      <h3 className="text-xl font-semibold text-gray-700">
        Booking #{booking.bookingID}: {booking.classroomName}
      </h3>
      <p className="text-gray-500 mb-6">
        {booking.bookingType} | {booking.startTime} - {booking.endTime}
      </p>

      {/* List of Dates */}
      <h4 className="text-lg font-semibold mb-4 text-gray-600">
        Scheduled Dates
      </h4>
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {booking.instances.map(instance => (
              <tr key={instance.date}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {new Date(instance.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      instance.status === "Active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {instance.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  {instance.canCancel ? (
                    <button
                      className="text-red-600 hover:text-red-900 flex items-center"
                      // 7. Update onClick to open the modal
                      onClick={() => openCancelModal(instance.date)}
                    >
                      <CalendarX className="w-4 h-4 mr-1" />
                      Cancel this day
                    </button>
                  ) : (
                    <span className="text-gray-400 text-xs italic">
                      {instance.status === "Cancellation"
                        ? "Cancelled"
                        : "Deadline Passed"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 8. Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Confirm Cancellation
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Are you sure you want to cancel the booking for{" "}
                <strong>{new Date(dateToCancel).toLocaleDateString()}</strong>?
                A credit will be issued to your account.
              </p>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              <button
                type="button"
                disabled={isCancelling}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 disabled:bg-red-300 transition"
                onClick={confirmCancellation}
              >
                {isCancelling ? "Cancelling..." : "Confirm"}
              </button>
              <button
                type="button"
                disabled={isCancelling}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 transition"
                onClick={closeModal}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingDetailsPage
